import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { Play } from 'lucide-react';
import { OnboardingStepProps } from '../../../types/onboarding';
import { ProfilePhoto } from '../../../components/Profile/types';
import { StepTitle, StepDescription, StepContent } from '../../../styles/onboarding/Onboarding.styles';
import { theme } from '../../../styles/theme';
import { logger } from '../../../utils/logger';
import { captureVideoThumbnail } from '../../../utils/media/captureVideoThumbnail';
import { profileService } from '../../../services/Profile/profileService';
import { uploadToStorage, MIME_TO_EXTENSION } from '../../../services/Profile/mediaUpload';
import { secureStorage } from '../../../services/Storage/secureStorage';

// --- Styled Components ---
const MediaGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;
  margin-top: 1rem;

  @media (min-width: ${theme.breakpoints.tablet}) {
    grid-template-columns: 1fr 1fr;
  }
`;

const UploadCard = styled.div<{ $hasContent: boolean; $error?: boolean }>`
  background: ${theme.colors.background.card};
  border: 2px dashed ${props => props.$error ? theme.colors.error : (props.$hasContent ? theme.colors.primary : theme.colors.border.medium)};
  border-radius: ${theme.borderRadius.lg};
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  overflow: hidden;

  &:hover {
    border-color: ${props => props.$error ? theme.colors.error : theme.colors.primary};
    background: ${theme.colors.background.secondary};
  }
`;

const PreviewImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  position: absolute;
  top: 0;
  left: 0;
`;

const PreviewVideo = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
  position: absolute;
  top: 0;
  left: 0;
`;

const IconWrapper = styled.div`
  font-size: 3rem;
  margin-bottom: 1rem;
  color: ${theme.colors.text.secondary};
  z-index: 1;
`;

const Label = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: ${theme.colors.text.primary};
  margin-bottom: 0.5rem;
  z-index: 1;
`;

const SubLabel = styled.p`
  font-size: 0.9rem;
  color: ${theme.colors.text.secondary};
  text-align: center;
  z-index: 1;
`;

const RequirementBadge = styled.span`
  background: ${theme.colors.primary};
  color: white;
  padding: 0.25rem 0.75rem;
  border-radius: 99px;
  font-size: 0.75rem;
  font-weight: 600;
  margin-top: 1rem;
  z-index: 1;
`;

const ErrorText = styled.div`
  color: ${theme.colors.error};
  font-size: 0.85rem;
  margin-top: 0.5rem;
  text-align: center;
  position: absolute;
  bottom: 1rem;
  width: 90%;
  z-index: 2;
  background: rgba(255, 255, 255, 0.9);
  padding: 0.25rem;
  border-radius: 4px;
`;

const PlayIconOverlay = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
  pointer-events: none;
`;

const RemoveButton = styled.button`
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  background: rgba(0, 0, 0, 0.6);
  color: white;
  border: none;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;
  font-size: 1.2rem;
  
  &:hover {
    background: rgba(220, 53, 69, 0.9);
  }
`;

export const PhotosStep: React.FC<OnboardingStepProps> = ({
  data,
  onChange
}) => {
  const [photos, setPhotos] = useState<ProfilePhoto[]>(data?.photos || []);
  const [imageError, setImageError] = useState<string | null>(null);
  const [videoError, setVideoError] = useState<string | null>(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [isUploadingVideo, setIsUploadingVideo] = useState(false);

  const imageInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);

  const userId = secureStorage.getUserData()?.id;

  // Load whatever the user has already stored, every time the step opens, so
  // re-entering onboarding never asks for media that is already saved.
  useEffect(() => {
    let cancelled = false;
    profileService.getProfile()
      .then(profile => {
        if (cancelled || !profile.photos?.length) return;
        setPhotos(prev => (prev.length ? prev : profile.photos));
      })
      .catch(err => logger.log('[ONBOARDING] No existing media to load:', err));
    return () => { cancelled = true; };
  }, []);

  // Identify main assets
  const mainImage = photos.find(p => p.type === 'image' && p.isMain);
  const mainVideo = photos.find(p => p.type === 'video' && p.isMain);

  useEffect(() => {
    // Both are STRICTLY required
    const isValid = !!mainImage && !!mainVideo;
    onChange({ photos }, isValid);
  }, [photos, mainImage, mainVideo, onChange]);

  const handleImageSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setImageError(null);
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setImageError('Please upload a valid image file');
      return;
    }

    if (file.size > 10 * 1024 * 1024) { // 10MB
      setImageError('Image must be smaller than 10MB');
      return;
    }

    if (!userId) {
      setImageError('Please sign in again before uploading.');
      return;
    }

    // Upload to storage and register the hosted URL with the backend. This
    // step used to keep only a local object URL, so an onboarding photo never
    // existed anywhere but the browser tab that picked it.
    setIsUploadingImage(true);
    try {
      const extension = MIME_TO_EXTENSION[file.type] || 'jpg';
      const url = await uploadToStorage(userId, file, extension, file.type);
      const saved = await profileService.addPhoto(url, 'image');
      setPhotos(prev => [...prev.filter(p => p.type !== 'image'), { ...saved, type: 'image', isMain: true }]);
    } catch (err: any) {
      logger.error('[ONBOARDING] Image upload failed:', err);
      setImageError(err?.message || 'Upload failed. Please try again.');
    } finally {
      setIsUploadingImage(false);
    }
  };

  const handleVideoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    setVideoError(null);
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('video/')) {
      setVideoError('Please upload a valid video file');
      return;
    }

    if (file.size > 50 * 1024 * 1024) { // 50MB limit for safety
      setVideoError('Video must be smaller than 50MB');
      return;
    }

    // Check duration via temporary video element
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.onloadedmetadata = async function () {
      window.URL.revokeObjectURL(video.src);
      const duration = video.duration;

      if (duration > 15.5) { // 0.5s buffer
        setVideoError('Video must be 15 seconds or less');
        return;
      }

      if (!userId) {
        setVideoError('Please sign in again before uploading.');
        return;
      }

      setIsUploadingVideo(true);
      try {
        const extension = MIME_TO_EXTENSION[file.type] || 'mp4';
        const url = await uploadToStorage(userId, file, extension, file.type);

        // Thumbnail is uploaded separately so the stored record points at a
        // hosted image rather than a blob URL that dies with the tab.
        let thumbnailUrl: string | undefined;
        const thumbnailBlob = await captureVideoThumbnail(file);
        if (thumbnailBlob) {
          thumbnailUrl = await uploadToStorage(userId, thumbnailBlob, 'jpg', 'image/jpeg');
        }

        const saved = await profileService.addPhoto(url, 'video', thumbnailUrl, Math.round(duration));
        setPhotos(prev => [
          ...prev.filter(p => p.type !== 'video'),
          { ...saved, type: 'video', isMain: true, duration, thumbnail: thumbnailUrl },
        ]);
      } catch (err: any) {
        logger.error('[ONBOARDING] Video upload failed:', err);
        setVideoError(err?.message || 'Upload failed. Please try again.');
      } finally {
        setIsUploadingVideo(false);
      }
    };
    video.src = URL.createObjectURL(file);
  };

  const removeMedia = (type: 'image' | 'video') => {
    // Delete server-side too, otherwise the record survives and the profile
    // still shows media the user just removed.
    const removed = photos.filter(p => p.type === type);
    removed.forEach(p => {
      if (!p.id.startsWith('img_') && !p.id.startsWith('vid_')) {
        profileService.deletePhoto(userId || '', p.id)
          .catch(err => logger.error('[ONBOARDING] Failed to delete media:', err));
      }
    });

    setPhotos(prev => prev.filter(p => p.type !== type));
    if (type === 'image') {
      setImageError(null);
      if (imageInputRef.current) imageInputRef.current.value = '';
    }
    if (type === 'video') {
      setVideoError(null);
      if (videoInputRef.current) videoInputRef.current.value = '';
    }
  };

  return (
    <>
      <StepTitle>Your Main Profile Media</StepTitle>
      <StepDescription>
        To ensure high quality connections, Plenty of Hearts requires a main profile photo AND a 15-second intro video.
      </StepDescription>

      <StepContent>
        <MediaGrid>
          {/* Main Image Upload */}
          <UploadCard
            $hasContent={!!mainImage}
            $error={!!imageError}
            onClick={() => !mainImage && !isUploadingImage && imageInputRef.current?.click()}
          >
            {isUploadingImage ? (
              <>
                <IconWrapper>⏳</IconWrapper>
                <Label>Uploading photo…</Label>
                <SubLabel>Saving to your profile</SubLabel>
              </>
            ) : mainImage ? (
              <>
                <PreviewImage src={mainImage.url} alt="Main Profile" />
                <RemoveButton onClick={(e) => { e.stopPropagation(); removeMedia('image'); }}>×</RemoveButton>
                <RequirementBadge style={{ position: 'absolute', bottom: '1rem', left: '1rem' }}>✓ Image Set</RequirementBadge>
              </>
            ) : (
              <>
                <IconWrapper>📸</IconWrapper>
                <Label>Main Photo</Label>
                <SubLabel>Clear, well-lit face photo</SubLabel>
                <RequirementBadge>Required</RequirementBadge>
                {imageError && <ErrorText>{imageError}</ErrorText>}
              </>
            )}
            <input
              type="file"
              ref={imageInputRef}
              onChange={handleImageSelect}
              accept="image/*"
              style={{ display: 'none' }}
            />
          </UploadCard>

          {/* Main Video Upload */}
          <UploadCard
            $hasContent={!!mainVideo}
            $error={!!videoError}
            onClick={() => !mainVideo && !isUploadingVideo && videoInputRef.current?.click()}
          >
            {isUploadingVideo ? (
              <>
                <IconWrapper>⏳</IconWrapper>
                <Label>Uploading video…</Label>
                <SubLabel>Saving to your profile</SubLabel>
              </>
            ) : mainVideo ? (
              <>
                {mainVideo.thumbnail ? (
                  <>
                    <PreviewImage src={mainVideo.thumbnail} alt="Video thumbnail" />
                    <PlayIconOverlay><Play size={28} color="white" fill="white" /></PlayIconOverlay>
                  </>
                ) : (
                  <PreviewVideo src={mainVideo.url} controls muted playsInline />
                )}
                <RemoveButton onClick={(e) => { e.stopPropagation(); removeMedia('video'); }}>×</RemoveButton>
                <RequirementBadge style={{ position: 'absolute', bottom: '1rem', left: '1rem' }}>✓ Video Set</RequirementBadge>
              </>
            ) : (
              <>
                <IconWrapper>🎥</IconWrapper>
                <Label>Intro Video</Label>
                <SubLabel>Max 15 seconds. Say hello!</SubLabel>
                <RequirementBadge>Required</RequirementBadge>
                {videoError && <ErrorText>{videoError}</ErrorText>}
              </>
            )}
            <input
              type="file"
              ref={videoInputRef}
              onChange={handleVideoSelect}
              accept="video/*"
              style={{ display: 'none' }}
            />
          </UploadCard>
        </MediaGrid>
      </StepContent>
    </>
  );
};
